import { BrandKit, defaultBrandKit } from "@redotech/redo-model/brand-kit";
import { getFontFamilyWithFallback } from "@redotech/redo-model/brand-kit/font-utils";
import { OrderLookupSettings } from "@redotech/redo-model/order-tracking/tracking-page-definition";
import { memo, useContext, useMemo, useState } from "react";
import { BrandKitButton } from "../brand-kit-button";
import { BrandKitTextInput } from "../brand-kit-text-input";
import { TeamContext } from "../context/team-context";
import { Flex } from "../flex";
import { Text } from "../text";
import * as styles from "./embedded-order-tracking-login.module.css";

export const EmbeddedOrderTrackingLogin = memo(
  function EmbeddedOrderTrackingLogin({
    settings,
    brandKit: brandKitProp,
    onSubmit,
    pending,
    error,
  }: {
    settings: OrderLookupSettings;
    /** Pass explicitly for storefront use (no TeamContext available). */
    brandKit: BrandKit | undefined;
    onSubmit:
      | ((emailOrZip: string, orderIdTrackingNumber: string) => void)
      | undefined;
    pending: boolean | undefined;
    error: string | undefined;
  }) {
    const team = useContext(TeamContext);
    const brandKit =
      brandKitProp ?? team?.settings?.brandKit ?? defaultBrandKit;
    const [emailZip, setEmailZip] = useState("");
    const [orderTrackingNumber, setOrderTrackingNumber] = useState("");

    const emailLabel = settings.emailLabel ?? "Email or zip code";
    const emailPlaceholder =
      settings.emailPlaceholder ?? "Enter email or zip...";
    const orderLabel = settings.orderLabel ?? "Order # or tracking #";
    const orderPlaceholder =
      settings.orderPlaceholder ?? "Enter order or tracking #...";

    const titleFontFamily = getFontFamilyWithFallback(
      settings.titleFontFamily ?? brandKit.font.headerFontFamily,
      brandKit.customFontFamilies,
    );
    const bodyFontFamily = getFontFamilyWithFallback(
      settings.bodyFontFamily ?? brandKit.font.bodyFontFamily,
      brandKit.customFontFamilies,
    );

    // Override brand kit fonts with order lookup settings so that
    // BrandKitButton and BrandKitTextInput use the configured fonts
    const effectiveBrandKit = useMemo(
      (): BrandKit => ({
        ...brandKit,
        font: {
          ...brandKit.font,
          bodyFontFamily:
            settings.bodyFontFamily ?? brandKit.font.bodyFontFamily,
          headerFontFamily:
            settings.titleFontFamily ?? brandKit.font.headerFontFamily,
        },
      }),
      [brandKit, settings.bodyFontFamily, settings.titleFontFamily],
    );

    const submitDisabled =
      onSubmit !== undefined && (!emailZip || !orderTrackingNumber || pending);

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      if (!submitDisabled && onSubmit) {
        onSubmit(emailZip, orderTrackingNumber);
      }
    };

    const inputOverride = settings.inputOverride?.enabled
      ? {
          ...settings.inputOverride,
          font: {
            ...settings.inputOverride.font,
            fontFamily:
              settings.bodyFontFamily ?? settings.inputOverride.font.fontFamily,
          },
        }
      : undefined;

    return (
      <div
        className={styles.background}
        style={{
          backgroundColor: settings.backgroundColor,
          backgroundImage: settings.backgroundImageUrl
            ? `url(${settings.backgroundImageUrl})`
            : undefined,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <form
          className={styles.card}
          onSubmit={handleSubmit}
          style={{
            backgroundColor: settings.cardBackgroundColor,
            borderRadius: settings.cardBorderRadius,
          }}
        >
          {settings.titleText && (
            <Text
              as="h2"
              fontSize="xl"
              fontWeight="semibold"
              style={{
                color: settings.titleColor,
                fontFamily: titleFontFamily,
                transition: "none",
              }}
            >
              {settings.titleText}
            </Text>
          )}

          <Flex dir="column" gap="lg">
            <Flex dir="column" gap="xs">
              <label
                className={styles.label}
                style={{
                  color: settings.titleColor,
                  fontFamily: bodyFontFamily,
                }}
              >
                {emailLabel}
              </label>
              <BrandKitTextInput
                brandKit={effectiveBrandKit}
                override={inputOverride}
                placeholder={emailPlaceholder}
                setValue={setEmailZip}
                value={emailZip}
              />
            </Flex>

            <Flex dir="column" gap="xs">
              <label
                className={styles.label}
                style={{
                  color: settings.titleColor,
                  fontFamily: bodyFontFamily,
                }}
              >
                {orderLabel}
              </label>
              <BrandKitTextInput
                brandKit={effectiveBrandKit}
                override={inputOverride}
                placeholder={orderPlaceholder}
                setValue={setOrderTrackingNumber}
                value={orderTrackingNumber}
              />
            </Flex>
          </Flex>

          <BrandKitButton
            brandKit={effectiveBrandKit}
            className={styles.button}
            disabled={submitDisabled}
            globalOverride={
              settings.buttonOverride?.enabled
                ? {
                    primary: {
                      ...settings.buttonOverride,
                      font: {
                        ...settings.buttonOverride.font,
                        fontFamily:
                          settings.bodyFontFamily ??
                          settings.buttonOverride.font.fontFamily,
                      },
                    },
                    secondary: settings.buttonOverride,
                    tertiary: settings.buttonOverride,
                  }
                : undefined
            }
            hierarchy="primary"
            pending={pending}
            text={settings.buttonText}
            type="submit"
          />

          {error && (
            <Text fontSize="sm" textColor="error">
              {error}
            </Text>
          )}
        </form>
      </div>
    );
  },
);
